// [PURPOSE]: Pure adapter — convert an authored `multiclassProficiencies` block
//   ({ armor, weapons, tools } with heterogeneous storage shapes) into a list of
//   effectRegistry `proficiency` grant effects that the armor/weapon/tool readers
//   already union via getGrantedProficiencyLevel.
// [WHY]: MULTICLASS-PROF-APPLY (2026-06-14). The authored field was INERT — read by
//   NO production code (classWeaponProf.test.js:18-20). This adapter is the
//   storage→registry-key bridge. Extracted (Rule 22) so the producer→register→reader
//   chain is unit-testable without mounting React.
//   Trace: .claude/findings/2026-06-14-datamodel-multiclass-proficiency-apply-mpa1.md.
//
// [GRANT VALUE FORMS] — must match the readers EXACTLY:
//   armor:light|medium|heavy|shield  (armorProficiency.js REGISTRY_ARMOR_TOKENS)
//   weapon:simple|martial            (weaponDamageFormula.js:141)
//   tool:<label-lowercased>          (toolProficiency.js:20)
//
// [STORAGE SHAPES IN] — heterogeneous (see CustomClassCreator handlers):
//   armor   → display strings: 'Light armor' | 'Medium armor' | 'Heavy armor' | 'Shields'
//   weapons → prefixed ids (custom: 'weapon:simple') OR display strings
//             (SRD multiclass data: 'Simple weapons' | 'Martial weapons')
//   tools   → plain display strings: "Thieves' tools", "Tinker's tools"
// Each branch is ALSO defensive against the already-keyed form so a future storage
// migration can't silently zero the grant.

import { armorLabelToRegistryKey } from './armorProficiency';

const mkGrant = (value) => ({ type: 'proficiency', operation: 'grant', value, proficiencyLevel: 1 });

/**
 * Convert an authored multiclassProficiencies block into proficiency grant effects.
 * @param {{armor?: string[], weapons?: string[], tools?: string[]}} mcProfs
 * @returns {Array<{type:string, operation:string, value:string, proficiencyLevel:number}>}
 *   deduped grant effects (one per distinct key)
 */
export function multiclassProfsToGrantEffects(mcProfs) {
  const src = mcProfs || {};
  const keys = [];

  // armor: display string → 'armor:<token>' (drops unmappable, e.g. 'All armor')
  (Array.isArray(src.armor) ? src.armor : []).forEach((label) => {
    if (typeof label !== 'string') return;
    const norm = label.toLowerCase().trim();
    if (norm.startsWith('armor:')) { keys.push(norm); return; }
    const key = armorLabelToRegistryKey(label);
    if (key) keys.push(key);
  });

  // weapons: prefixed ids (custom 'weapon:simple') OR display strings (SRD
  //   'Simple weapons' / 'Martial weapons'). Specific-weapon grants (e.g. Monk
  //   'shortswords') aren't category tokens -> dropped here (the apply layer only
  //   models simple/martial categories; see MULTICLASS-PROF-SRD-CHOICE-GRANTS).
  (Array.isArray(src.weapons) ? src.weapons : []).forEach((w) => {
    if (typeof w !== 'string') return;
    const norm = w.toLowerCase().trim();
    if (norm.startsWith('weapon:')) { keys.push(norm); return; }
    if (norm.includes('simple')) keys.push('weapon:simple');
    else if (norm.includes('martial')) keys.push('weapon:martial');
  });

  // tools: display string → 'tool:<lowercased>' (matches toolProficiency.js:20 exactly)
  (Array.isArray(src.tools) ? src.tools : []).forEach((t) => {
    if (typeof t !== 'string' || !t.trim()) return;
    const norm = t.toLowerCase().trim();
    keys.push(norm.startsWith('tool:') ? norm : `tool:${norm}`);
  });

  // dedupe by value — a class listing the same prof twice registers one grant
  return Array.from(new Set(keys)).map(mkGrant);
}

export default multiclassProfsToGrantEffects;
